type ChunkReloadWindow = {
  location: {
    href: string;
    reload: () => void;
  };
  sessionStorage?: Pick<Storage, 'getItem' | 'setItem'>;
};

type ServiceWorkerRegistrationLike = {
  unregister: () => Promise<boolean> | boolean;
};

type ServiceWorkerCleanupEnvironment = {
  navigator?: {
    serviceWorker?: {
      getRegistrations?: () => Promise<readonly ServiceWorkerRegistrationLike[]>;
    };
  };
  caches?: {
    keys?: () => Promise<string[]>;
    delete?: (key: string) => Promise<boolean> | boolean;
  };
};

const chunkReloadStorageKeyPrefix = 'cocodakara:chunk-load-reloaded:';

export const isChunkLoadError = (error: unknown): boolean => {
  const candidate = error as { name?: unknown; message?: unknown };
  const text = [candidate?.name, candidate?.message, String(error)].filter(Boolean).join(' ');

  return [
    /ChunkLoadError/i,
    /Loading chunk [\w-]+ failed/i,
    /CSS_CHUNK_LOAD_FAILED/i,
    /failed to fetch dynamically imported module/i,
    /Importing a module script failed/i,
    /Unexpected token '<'/i,
  ].some((pattern) => pattern.test(text));
};

export const reloadOnceForChunkLoadError = (error: unknown, win?: ChunkReloadWindow): boolean => {
  if (!isChunkLoadError(error)) return false;

  const browserWindow = win ?? (typeof window !== 'undefined' ? window : undefined);
  if (!browserWindow) return false;

  const key = `${chunkReloadStorageKeyPrefix}${browserWindow.location.href}`;
  try {
    if (browserWindow.sessionStorage?.getItem(key) === '1') return false;
    browserWindow.sessionStorage?.setItem(key, '1');
  } catch {
    return false;
  }

  browserWindow.location.reload();
  return true;
};

const currentEnvironment = (): ServiceWorkerCleanupEnvironment | undefined => {
  if (typeof window === 'undefined') return undefined;
  return window;
};

export const cleanupLegacyServiceWorker = async (
  env: ServiceWorkerCleanupEnvironment | undefined = currentEnvironment(),
): Promise<void> => {
  if (!env) return;

  const registrations = await env.navigator?.serviceWorker?.getRegistrations?.().catch(() => []);
  await Promise.all(
    (registrations ?? []).map((registration) => Promise.resolve(registration.unregister()).catch(() => false)),
  );

  const cacheKeys = await env.caches?.keys?.().catch(() => []);
  await Promise.all((cacheKeys ?? []).map((key) => Promise.resolve(env.caches?.delete?.(key)).catch(() => false)));
};
